import { useState, useEffect, useRef } from "react";
import { createPortal } from "react-dom";
import { settingsApi } from "@/integrations/mysql/client";
import { useToast } from "@/hooks/use-toast";
import {
  Save, Loader2, ClipboardList, Search, Sparkles, ThumbsUp,
  Target, Users, CheckCircle, FileText, Calendar, ArrowRight,
  Star, Heart, Brain, Shield, Zap, Plus, Trash2, Play, type LucideIcon,
} from "lucide-react";
import { PageVisibilityToggle } from "@/components/ui/PageVisibilityToggle";

// ── Icon map ───────────────────────────────────────────────────────────────────
const ICON_MAP: Record<string, LucideIcon> = {
  ClipboardList, Search, Sparkles, ThumbsUp,
  Target, Users, CheckCircle, FileText, Calendar, ArrowRight,
  Star, Heart, Brain, Shield, Zap,
};
const ICON_NAMES = Object.keys(ICON_MAP);

// ── Shared UI ──────────────────────────────────────────────────────────────────
function Field({ label, hint, children }: { label: string; hint?: string; children: React.ReactNode }) {
  return (
    <div className="space-y-1">
      <label className="text-xs font-semibold text-gray-600 uppercase tracking-wide">{label}</label>
      {hint && <p className="text-xs text-gray-400">{hint}</p>}
      {children}
    </div>
  );
}

function TextInput({ value, onChange, placeholder, maxLength, type = "text" }: {
  value: string; onChange: (v: string) => void; placeholder?: string; maxLength?: number; type?: string;
}) {
  return (
    <input
      type={type} value={value} onChange={e => onChange(e.target.value)}
      placeholder={placeholder} maxLength={maxLength}
      className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-brand-primary/30 focus:border-brand-primary transition-all"
    />
  );
}

function TextArea({ value, onChange, placeholder, rows = 3, maxLength }: {
  value: string; onChange: (v: string) => void; placeholder?: string; rows?: number; maxLength?: number;
}) {
  return (
    <textarea
      value={value} onChange={e => onChange(e.target.value)}
      placeholder={placeholder} rows={rows} maxLength={maxLength}
      className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-brand-primary/30 focus:border-brand-primary transition-all resize-none"
    />
  );
}

function SectionHeader({ icon: Icon, title, description }: { icon: React.ElementType; title: string; description: string }) {
  return (
    <div className="flex items-center gap-3 pb-3 border-b border-gray-100 mb-4">
      <div className="w-8 h-8 rounded-lg bg-brand-primary/8 flex items-center justify-center shrink-0">
        <Icon className="w-4 h-4 text-brand-primary" />
      </div>
      <div>
        <h3 className="text-sm font-bold text-gray-800">{title}</h3>
        <p className="text-xs text-gray-400">{description}</p>
      </div>
    </div>
  );
}

function SaveBar({ dirty, saving, onSave }: { dirty: boolean; saving: boolean; onSave: () => void }) {
  if (!dirty) return null;
  return (
    <div className="flex items-center justify-between bg-brand-primary/5 border border-brand-primary/20 rounded-xl px-4 py-3 mt-2">
      <p className="text-xs text-brand-primary font-medium">Alterações não salvas</p>
      <button
        onClick={onSave} disabled={saving}
        className="flex items-center gap-1.5 bg-brand-primary hover:bg-brand-primary/90 text-white text-xs font-semibold px-3 py-1.5 rounded-lg transition-colors disabled:opacity-60"
      >
        {saving ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Save className="w-3.5 h-3.5" />}
        Salvar
      </button>
    </div>
  );
}

// ── Icon picker ────────────────────────────────────────────────────────────────
function IconPickerInline({ value, onChange }: { value: string; onChange: (v: string) => void }) {
  const [open, setOpen] = useState(false);
  const [pos, setPos] = useState({ top: 0, left: 0 });
  const btnRef = useRef<HTMLButtonElement>(null);
  const Icon = ICON_MAP[value] || Star;

  const handleOpen = () => {
    if (!open && btnRef.current) {
      const r = btnRef.current.getBoundingClientRect();
      setPos({ top: r.bottom + 6, left: r.left });
    }
    setOpen(v => !v);
  };

  useEffect(() => {
    if (!open) return;
    const close = () => setOpen(false);
    document.addEventListener('mousedown', close);
    return () => document.removeEventListener('mousedown', close);
  }, [open]);

  return (
    <div className="relative shrink-0">
      <button
        ref={btnRef}
        type="button"
        onClick={handleOpen}
        className={`w-8 h-8 flex items-center justify-center rounded-lg border transition-colors ${
          open ? "border-brand-primary bg-brand-primary/8" : "border-gray-200 bg-white hover:border-brand-primary/50"
        }`}
        title={`Ícone: ${value}`}
      >
        <Icon className="w-4 h-4 text-brand-primary" />
      </button>
      {open && createPortal(
        <div
          onMouseDown={e => e.stopPropagation()}
          style={{ position: 'fixed', top: pos.top, left: pos.left, zIndex: 9999 }}
          className="bg-white border border-gray-200 rounded-xl shadow-xl p-2 w-56"
        >
          <div className="grid grid-cols-5 gap-1">
            {ICON_NAMES.map(name => {
              const Ic = ICON_MAP[name];
              return (
                <button
                  key={name}
                  type="button"
                  title={name}
                  onClick={() => { onChange(name); setOpen(false); }}
                  className={`w-8 h-8 flex items-center justify-center rounded-lg transition-all ${
                    value === name
                      ? "bg-brand-primary text-white"
                      : "text-gray-500 hover:bg-brand-primary/10 hover:text-brand-primary"
                  }`}
                >
                  <Ic className="w-4 h-4" />
                </button>
              );
            })}
          </div>
        </div>,
        document.body
      )}
    </div>
  );
}

// ── Default values (from AvaliacaoCaso.tsx hardcoded text) ────────────────────
interface BadgeItem { label: string; icon: string; }
interface HeroData {
  label: string;
  labelIcon: string;
  title: string;
  subtitle: string;
  badges: BadgeItem[];
}

interface OQueEData {
  greenTitle: string;
  greenDesc: string;
  greenButtonText: string;
  desc1: string;
  desc2: string;
  bullets: string[];
}

interface StepItem {
  icon: string;
  num: string;
  title: string;
  desc: string;
}

interface ProfileItem {
  icon: string;
  title: string;
  desc: string;
}

interface CtaData {
  title: string;
  desc: string;
  btn1Text: string;
  btn1Url: string;
  btn2Text: string;
  btn2Url: string;
}

const DEFAULT_HERO: HeroData = {
  label: "100% Gratuita",
  labelIcon: "FileText",
  title: "Avaliação de Caso",
  subtitle: "Análise personalizada e gratuita do seu perfil nutricional para definir o melhor plano de tratamento.",
  badges: [
    { label: "Sem compromisso",   icon: "CheckCircle" },
    { label: "Resposta em 24h",   icon: "CheckCircle" },
    { label: "Plano personalizado", icon: "CheckCircle" },
  ],
};

const DEFAULT_OQUE: OQueEData = {
  greenTitle: "O que é a Avaliação de Caso?",
  greenDesc: "Um processo inicial para analisar seu histórico, hábitos e objetivos — sem nenhum custo.",
  greenButtonText: "Solicitar Avaliação",
  desc1: "A Avaliação de Caso é um processo inicial onde analisamos seu histórico de saúde, hábitos alimentares, estilo de vida e objetivos para criar uma estratégia nutricional personalizada e eficaz.",
  desc2: "Durante essa análise, identificamos as melhores abordagens para seu perfil, considerando suas necessidades específicas, preferências alimentares e rotina diária.",
  bullets: [
    "Análise completa do histórico alimentar",
    "Identificação de objetivos e necessidades",
    "Proposta de plano de tratamento personalizado",
    "Orientações iniciais sem compromisso",
  ],
};

const DEFAULT_STEPS: StepItem[] = [
  { icon: "ClipboardList", num: "1", title: "Questionário",  desc: "Preencha nosso formulário detalhado sobre saúde, hábitos e objetivos." },
  { icon: "Search",        num: "2", title: "Análise",       desc: "Nossa equipe analisa suas informações de forma criteriosa." },
  { icon: "Sparkles",      num: "3", title: "Proposta",      desc: "Você recebe uma proposta personalizada de tratamento." },
  { icon: "ThumbsUp",      num: "4", title: "Decisão",       desc: "Você decide com tranquilidade se quer iniciar o acompanhamento." },
];

const DEFAULT_PROFILES: ProfileItem[] = [
  { icon: "Target",       title: "Objetivos Específicos", desc: "Emagrecimento, ganho de massa muscular, melhora da saúde ou performance esportiva." },
  { icon: "Users",        title: "Famílias",              desc: "Famílias que querem estabelecer hábitos alimentares saudáveis para todos os membros." },
  { icon: "CheckCircle",  title: "Condições de Saúde",    desc: "Diabetes, hipertensão, colesterol alto ou outras condições que exigem atenção nutricional." },
];

const DEFAULT_CTA: CtaData = {
  title: "Pronto para começar?",
  desc: "Entre em contato pelo WhatsApp ou agende sua avaliação gratuita diretamente pela agenda online.",
  btn1Text: "Agendar Avaliação Gratuita",
  btn1Url: "https://calendar.app.google/ULCXQLbU7iFtuVHh7",
  btn2Text: "Falar no WhatsApp",
  btn2Url: "https://wa.me/5511998874081",
};

// ── Tab: Cabeçalho ─────────────────────────────────────────────────────────────
function CabecalhoTab() {
  const { toast } = useToast();
  const [data, setData]   = useState<HeroData>(DEFAULT_HERO);
  const [badgeDraft, setBadgeDraft] = useState('');
  const [loading, setLoading] = useState(true);
  const [saving, setSaving]   = useState(false);
  const [dirty, setDirty]     = useState(false);

  useEffect(() => {
    settingsApi.list()
      .then(list => {
        const raw = list.find(s => s.key === 'avaliacoes_hero')?.value;
        if (raw) {
          try { setData({ ...DEFAULT_HERO, ...JSON.parse(raw) }); } catch { /**/ }
        }
      })
      .catch(() => {})
      .finally(() => setLoading(false));
  }, []);

  const upd = <K extends keyof HeroData>(key: K, val: HeroData[K]) => {
    setData(prev => ({ ...prev, [key]: val }));
    setDirty(true);
  };

  const addBadge = () => {
    const t = badgeDraft.trim();
    if (!t) return;
    upd('badges', [...data.badges, { label: t, icon: 'CheckCircle' }]);
    setBadgeDraft('');
  };

  const editBadge = (i: number, patch: Partial<BadgeItem>) => {
    const arr = [...data.badges];
    arr[i] = { ...arr[i], ...patch };
    upd('badges', arr);
  };

  const removeBadge = (i: number) => {
    upd('badges', data.badges.filter((_, idx) => idx !== i));
  };

  // backward-compat: stored badges may be plain strings
  const normBadges = (data.badges as any[]).map(b =>
    typeof b === 'string' ? { label: b, icon: 'CheckCircle' } : b
  ) as BadgeItem[];

  const handleSave = async () => {
    setSaving(true);
    try {
      await settingsApi.update('avaliacoes_hero', JSON.stringify(data));
      toast({ title: 'Salvo!' });
      setDirty(false);
    } catch {
      toast({ title: 'Erro ao salvar', variant: 'destructive' });
    } finally { setSaving(false); }
  };

  if (loading) return <div className="flex items-center justify-center py-12"><Loader2 className="w-5 h-5 animate-spin text-brand-primary" /></div>;

  return (
    <div className="space-y-4 bg-white border border-gray-100 rounded-xl p-4">
      <SectionHeader icon={ClipboardList} title="Cabeçalho da Página" description="Seção hero no topo da página /avaliacao" />

      <Field label="Pílula / Label">
        <div className="flex items-center gap-2">
          <IconPickerInline value={data.labelIcon ?? 'FileText'} onChange={v => upd('labelIcon', v)} />
          <TextInput value={data.label} onChange={v => upd('label', v)} placeholder="100% Gratuita" maxLength={40} />
        </div>
      </Field>
      <Field label="Título">
        <TextInput value={data.title} onChange={v => upd('title', v)} placeholder="Avaliação de Caso" maxLength={100} />
      </Field>
      <Field label={`Subtítulo (${data.subtitle.length}/300)`}>
        <TextArea value={data.subtitle} onChange={v => upd('subtitle', v)} rows={3} maxLength={300} placeholder="Análise personalizada e gratuita..." />
      </Field>

      {/* Badges */}
      <div className="space-y-2">
        <div className="flex items-center justify-between">
          <label className="text-xs font-semibold text-gray-600 uppercase tracking-wide">Badges ({data.badges.length})</label>
          <span className="text-xs text-gray-400">Chips exibidos abaixo do subtítulo</span>
        </div>
        {normBadges.map((b, i) => (
          <div key={i} className="flex items-center gap-2">
            <IconPickerInline value={b.icon} onChange={icon => editBadge(i, { icon })} />
            <input
              type="text" value={b.label} onChange={e => editBadge(i, { label: e.target.value })} maxLength={60}
              className="flex-1 border border-gray-200 rounded-lg px-3 py-1.5 text-sm focus:outline-none focus:ring-2 focus:ring-brand-primary/30 focus:border-brand-primary transition-all"
            />
            <button onClick={() => removeBadge(i)} className="p-1.5 rounded-lg hover:bg-red-50 transition-colors"><Trash2 className="w-3.5 h-3.5 text-red-400" /></button>
          </div>
        ))}
        <div className="flex gap-2">
          <input
            type="text" value={badgeDraft} onChange={e => setBadgeDraft(e.target.value)}
            onKeyDown={e => { if (e.key === 'Enter') { e.preventDefault(); addBadge(); } }}
            placeholder="Ex: Sem compromisso" maxLength={60}
            className="flex-1 border border-gray-200 rounded-lg px-3 py-1.5 text-sm focus:outline-none focus:ring-2 focus:ring-brand-primary/30 transition-all"
          />
          <button onClick={addBadge} className="flex items-center gap-1.5 bg-gray-100 hover:bg-brand-primary/10 text-gray-600 hover:text-brand-primary text-xs font-medium px-3 py-1.5 rounded-lg transition-colors">
            <Plus className="w-3.5 h-3.5" /> Adicionar
          </button>
        </div>
      </div>

      <SaveBar dirty={dirty} saving={saving} onSave={handleSave} />
    </div>
  );
}

// ── Tab: O que é ───────────────────────────────────────────────────────────────
function OQueETab() {
  const { toast } = useToast();
  const [data, setData]   = useState<OQueEData>(DEFAULT_OQUE);
  const [bulletDraft, setBulletDraft] = useState('');
  const [loading, setLoading] = useState(true);
  const [saving, setSaving]   = useState(false);
  const [dirty, setDirty]     = useState(false);

  useEffect(() => {
    settingsApi.list()
      .then(list => {
        const raw = list.find(s => s.key === 'avaliacoes_oque')?.value;
        if (raw) {
          try { setData({ ...DEFAULT_OQUE, ...JSON.parse(raw) }); } catch { /**/ }
        }
      })
      .catch(() => {})
      .finally(() => setLoading(false));
  }, []);

  const upd = <K extends keyof OQueEData>(key: K, val: OQueEData[K]) => {
    setData(prev => ({ ...prev, [key]: val }));
    setDirty(true);
  };

  const addBullet = () => {
    const t = bulletDraft.trim();
    if (!t) return;
    upd('bullets', [...data.bullets, t]);
    setBulletDraft('');
  };

  const editBullet = (i: number, v: string) => {
    const arr = [...data.bullets];
    arr[i] = v;
    upd('bullets', arr);
  };

  const removeBullet = (i: number) => {
    upd('bullets', data.bullets.filter((_, idx) => idx !== i));
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      await settingsApi.update('avaliacoes_oque', JSON.stringify(data));
      toast({ title: 'Salvo!' });
      setDirty(false);
    } catch {
      toast({ title: 'Erro ao salvar', variant: 'destructive' });
    } finally { setSaving(false); }
  };

  if (loading) return <div className="flex items-center justify-center py-12"><Loader2 className="w-5 h-5 animate-spin text-brand-primary" /></div>;

  return (
    <div className="space-y-4 bg-white border border-gray-100 rounded-xl p-4">
      <SectionHeader icon={FileText} title="O que é a Avaliação" description="Seção com painel verde e painel branco" />

      <p className="text-xs font-bold text-gray-400 uppercase tracking-wide">Painel verde (esquerda)</p>
      <Field label="Título">
        <TextInput value={data.greenTitle} onChange={v => upd('greenTitle', v)} placeholder="O que é a Avaliação de Caso?" maxLength={100} />
      </Field>
      <Field label="Descrição curta">
        <TextArea value={data.greenDesc} onChange={v => upd('greenDesc', v)} rows={3} maxLength={300} placeholder="Um processo inicial para analisar..." />
      </Field>
      <Field label="Texto do botão">
        <TextInput value={data.greenButtonText} onChange={v => upd('greenButtonText', v)} placeholder="Solicitar Avaliação" maxLength={60} />
      </Field>

      <div className="h-px bg-gray-100 my-2" />
      <p className="text-xs font-bold text-gray-400 uppercase tracking-wide">Painel branco (direita)</p>
      <Field label="Parágrafo 1">
        <TextArea value={data.desc1} onChange={v => upd('desc1', v)} rows={4} maxLength={500} placeholder="A Avaliação de Caso é um processo inicial..." />
      </Field>
      <Field label="Parágrafo 2">
        <TextArea value={data.desc2} onChange={v => upd('desc2', v)} rows={4} maxLength={500} placeholder="Durante essa análise, identificamos..." />
      </Field>

      {/* Bullets */}
      <div className="space-y-2">
        <div className="flex items-center justify-between">
          <label className="text-xs font-semibold text-gray-600 uppercase tracking-wide">Lista de itens ({data.bullets.length})</label>
        </div>
        {data.bullets.map((b, i) => (
          <div key={i} className="flex items-center gap-2">
            <input
              type="text" value={b} onChange={e => editBullet(i, e.target.value)} maxLength={120}
              className="flex-1 border border-gray-200 rounded-lg px-3 py-1.5 text-sm focus:outline-none focus:ring-2 focus:ring-brand-primary/30 focus:border-brand-primary transition-all"
            />
            <button onClick={() => removeBullet(i)} className="p-1.5 rounded-lg hover:bg-red-50 transition-colors"><Trash2 className="w-3.5 h-3.5 text-red-400" /></button>
          </div>
        ))}
        <div className="flex gap-2">
          <input
            type="text" value={bulletDraft} onChange={e => setBulletDraft(e.target.value)}
            onKeyDown={e => { if (e.key === 'Enter') { e.preventDefault(); addBullet(); } }}
            placeholder="Ex: Análise completa do histórico alimentar" maxLength={120}
            className="flex-1 border border-gray-200 rounded-lg px-3 py-1.5 text-sm focus:outline-none focus:ring-2 focus:ring-brand-primary/30 transition-all"
          />
          <button onClick={addBullet} className="flex items-center gap-1.5 bg-gray-100 hover:bg-brand-primary/10 text-gray-600 hover:text-brand-primary text-xs font-medium px-3 py-1.5 rounded-lg transition-colors">
            <Plus className="w-3.5 h-3.5" /> Adicionar
          </button>
        </div>
      </div>

      <SaveBar dirty={dirty} saving={saving} onSave={handleSave} />
    </div>
  );
}

// ── Tab: Como Funciona ─────────────────────────────────────────────────────────
function ComoFuncionaTab() {
  const { toast } = useToast();
  const [steps, setSteps] = useState<StepItem[]>(DEFAULT_STEPS);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving]   = useState(false);
  const [dirty, setDirty]     = useState(false);

  useEffect(() => {
    settingsApi.list()
      .then(list => {
        const raw = list.find(s => s.key === 'avaliacoes_steps')?.value;
        if (raw) {
          try {
            const parsed = JSON.parse(raw);
            if (Array.isArray(parsed)) setSteps(parsed);
          } catch { /**/ }
        }
      })
      .catch(() => {})
      .finally(() => setLoading(false));
  }, []);

  const updStep = <K extends keyof StepItem>(i: number, key: K, val: StepItem[K]) => {
    setSteps(prev => prev.map((s, idx) => idx === i ? { ...s, [key]: val } : s));
    setDirty(true);
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      await settingsApi.update('avaliacoes_steps', JSON.stringify(steps));
      toast({ title: 'Salvo!' });
      setDirty(false);
    } catch {
      toast({ title: 'Erro ao salvar', variant: 'destructive' });
    } finally { setSaving(false); }
  };

  if (loading) return <div className="flex items-center justify-center py-12"><Loader2 className="w-5 h-5 animate-spin text-brand-primary" /></div>;

  return (
    <div className="space-y-4 bg-white border border-gray-100 rounded-xl p-4">
      <SectionHeader icon={Search} title="Como Funciona" description="4 passos do processo de avaliação" />

      {steps.map((step, i) => (
        <div key={i} className="border border-gray-100 rounded-xl p-4 space-y-3">
          <div className="flex items-center gap-2">
            <span className="text-xs font-bold text-brand-primary bg-brand-primary/8 px-2 py-0.5 rounded-full">Passo {step.num}</span>
            <IconPickerInline value={step.icon} onChange={v => updStep(i, 'icon', v)} />
          </div>
          <div className="grid grid-cols-2 gap-3">
            <Field label="Número exibido">
              <TextInput value={step.num} onChange={v => updStep(i, 'num', v)} placeholder="1" maxLength={2} />
            </Field>
            <Field label="Título">
              <TextInput value={step.title} onChange={v => updStep(i, 'title', v)} placeholder="Questionário" maxLength={60} />
            </Field>
          </div>
          <Field label="Descrição">
            <TextArea value={step.desc} onChange={v => updStep(i, 'desc', v)} rows={2} maxLength={200} placeholder="Descrição do passo..." />
          </Field>
        </div>
      ))}

      <SaveBar dirty={dirty} saving={saving} onSave={handleSave} />
    </div>
  );
}

// ── Tab: Para Quem é ───────────────────────────────────────────────────────────
function ParaQuemTab() {
  const { toast } = useToast();
  const [profiles, setProfiles] = useState<ProfileItem[]>(DEFAULT_PROFILES);
  const [loading, setLoading]   = useState(true);
  const [saving, setSaving]     = useState(false);
  const [dirty, setDirty]       = useState(false);

  useEffect(() => {
    settingsApi.list()
      .then(list => {
        const raw = list.find(s => s.key === 'avaliacoes_profiles')?.value;
        if (raw) {
          try {
            const parsed = JSON.parse(raw);
            if (Array.isArray(parsed)) setProfiles(parsed);
          } catch { /**/ }
        }
      })
      .catch(() => {})
      .finally(() => setLoading(false));
  }, []);

  const updProfile = <K extends keyof ProfileItem>(i: number, key: K, val: ProfileItem[K]) => {
    setProfiles(prev => prev.map((p, idx) => idx === i ? { ...p, [key]: val } : p));
    setDirty(true);
  };

  const addProfile = () => {
    if (profiles.length >= 5) return;
    setProfiles(prev => [...prev, { icon: 'Star', title: '', desc: '' }]);
    setDirty(true);
  };

  const removeProfile = (i: number) => {
    setProfiles(prev => prev.filter((_, idx) => idx !== i));
    setDirty(true);
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      await settingsApi.update('avaliacoes_profiles', JSON.stringify(profiles));
      toast({ title: 'Salvo!' });
      setDirty(false);
    } catch {
      toast({ title: 'Erro ao salvar', variant: 'destructive' });
    } finally { setSaving(false); }
  };

  if (loading) return <div className="flex items-center justify-center py-12"><Loader2 className="w-5 h-5 animate-spin text-brand-primary" /></div>;

  return (
    <div className="space-y-4 bg-white border border-gray-100 rounded-xl p-4">
      <SectionHeader icon={Users} title="Para Quem é" description="Cards de perfis indicados para a avaliação (máx. 5)" />

      {profiles.map((profile, i) => (
        <div key={i} className="border border-gray-100 rounded-xl p-4 space-y-3">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2">
              <span className="text-xs font-bold text-gray-400">Card {i + 1}</span>
              <IconPickerInline value={profile.icon} onChange={v => updProfile(i, 'icon', v)} />
            </div>
            {profiles.length > 1 && (
              <button onClick={() => removeProfile(i)} className="p-1.5 rounded-lg hover:bg-red-50 transition-colors">
                <Trash2 className="w-3.5 h-3.5 text-red-400" />
              </button>
            )}
          </div>
          <Field label="Título">
            <TextInput value={profile.title} onChange={v => updProfile(i, 'title', v)} placeholder="Objetivos Específicos" maxLength={80} />
          </Field>
          <Field label="Descrição">
            <TextArea value={profile.desc} onChange={v => updProfile(i, 'desc', v)} rows={2} maxLength={200} placeholder="Descrição do perfil..." />
          </Field>
        </div>
      ))}

      {profiles.length < 5 && (
        <button
          onClick={addProfile}
          className="w-full flex items-center justify-center gap-2 border-2 border-dashed border-gray-200 hover:border-brand-primary/40 hover:bg-brand-primary/5 text-gray-400 hover:text-brand-primary text-sm font-medium py-2.5 rounded-xl transition-all"
        >
          <Plus className="w-4 h-4" /> Adicionar card
        </button>
      )}

      <SaveBar dirty={dirty} saving={saving} onSave={handleSave} />
    </div>
  );
}

// ── Tab: CTA ───────────────────────────────────────────────────────────────────
function CtaTab() {
  const { toast } = useToast();
  const [data, setData]   = useState<CtaData>(DEFAULT_CTA);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving]   = useState(false);
  const [dirty, setDirty]     = useState(false);

  useEffect(() => {
    settingsApi.list()
      .then(list => {
        const raw = list.find(s => s.key === 'avaliacoes_cta')?.value;
        if (raw) {
          try { setData({ ...DEFAULT_CTA, ...JSON.parse(raw) }); } catch { /**/ }
        }
      })
      .catch(() => {})
      .finally(() => setLoading(false));
  }, []);

  const upd = <K extends keyof CtaData>(key: K, val: CtaData[K]) => {
    setData(prev => ({ ...prev, [key]: val }));
    setDirty(true);
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      await settingsApi.update('avaliacoes_cta', JSON.stringify(data));
      toast({ title: 'Salvo!' });
      setDirty(false);
    } catch {
      toast({ title: 'Erro ao salvar', variant: 'destructive' });
    } finally { setSaving(false); }
  };

  if (loading) return <div className="flex items-center justify-center py-12"><Loader2 className="w-5 h-5 animate-spin text-brand-primary" /></div>;

  return (
    <div className="space-y-4 bg-white border border-gray-100 rounded-xl p-4">
      <SectionHeader icon={ArrowRight} title="CTA Final" description="Seção de chamada para ação no rodapé da página" />

      <Field label="Título">
        <TextInput value={data.title} onChange={v => upd('title', v)} placeholder="Pronto para começar?" maxLength={100} />
      </Field>
      <Field label={`Descrição (${data.desc.length}/300)`}>
        <TextArea value={data.desc} onChange={v => upd('desc', v)} rows={3} maxLength={300} placeholder="Entre em contato pelo WhatsApp..." />
      </Field>

      <div className="h-px bg-gray-100 my-2" />
      <p className="text-xs font-bold text-gray-400 uppercase tracking-wide">Botão 1 (agendar)</p>
      <Field label="Texto do botão 1">
        <TextInput value={data.btn1Text} onChange={v => upd('btn1Text', v)} placeholder="Agendar Avaliação Gratuita" maxLength={60} />
      </Field>
      <Field label="URL do botão 1" hint="Link do Google Calendar ou outra página de agendamento">
        <TextInput value={data.btn1Url} onChange={v => upd('btn1Url', v)} placeholder="https://calendar.app.google/..." type="url" />
      </Field>

      <div className="h-px bg-gray-100 my-2" />
      <p className="text-xs font-bold text-gray-400 uppercase tracking-wide">Botão 2 (WhatsApp)</p>
      <Field label="Texto do botão 2">
        <TextInput value={data.btn2Text} onChange={v => upd('btn2Text', v)} placeholder="Falar no WhatsApp" maxLength={60} />
      </Field>
      <Field label="URL do botão 2">
        <TextInput value={data.btn2Url} onChange={v => upd('btn2Url', v)} placeholder="https://wa.me/55..." type="url" />
      </Field>

      <SaveBar dirty={dirty} saving={saving} onSave={handleSave} />
    </div>
  );
}

// ── Video Tab ──────────────────────────────────────────────────────────────────
function VideoTab() {
  const { toast } = useToast();
  const [url, setUrl] = useState('');
  const [dirty, setDirty] = useState(false);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    settingsApi.list().then(list => {
      const row = list.find((s: any) => s.key === 'avaliacoes_video');
      if (row?.value) setUrl(row.value);
    });
  }, []);

  const handleChange = (v: string) => { setUrl(v); setDirty(true); };

  const handleSave = async () => {
    setSaving(true);
    try {
      await settingsApi.update('avaliacoes_video', url);
      setDirty(false);
      toast({ title: 'Vídeo salvo!' });
    } catch {
      toast({ title: 'Erro ao salvar', variant: 'destructive' });
    } finally { setSaving(false); }
  };

  return (
    <div className="space-y-4">
      <SectionHeader icon={Play} title="Vídeo Convite" description="Vídeo exibido no final da página de Avaliação de Caso" />
      <Field label="URL do vídeo">
        <TextInput value={url} onChange={handleChange} placeholder="https://youtu.be/..." type="url" />
      </Field>

      {/* Behavior info box */}
      <div className="bg-blue-50 border border-blue-100 rounded-xl p-4 space-y-2.5 text-xs text-blue-800">
        <p className="font-semibold text-blue-900 text-[11px] uppercase tracking-wide">Como o vídeo é exibido no site</p>
        <div className="space-y-1.5">
          <div className="flex items-start gap-2">
            <span className="mt-0.5 w-4 h-4 rounded bg-blue-200 flex items-center justify-center shrink-0 text-[10px] font-bold">▶</span>
            <span><span className="font-semibold">YouTube</span> — vídeo incorporado diretamente na página (player embutido).</span>
          </div>
          <div className="flex items-start gap-2">
            <span className="mt-0.5 w-4 h-4 rounded bg-blue-200 flex items-center justify-center shrink-0 text-[10px] font-bold">↗</span>
            <span><span className="font-semibold">Instagram / TikTok / Facebook</span> — exibe um botão que abre o vídeo diretamente nessas plataformas (elas não permitem incorporação).</span>
          </div>
          <div className="flex items-start gap-2">
            <span className="mt-0.5 w-4 h-4 rounded bg-blue-200 flex items-center justify-center shrink-0 text-[10px] font-bold">▶</span>
            <div>
              <span><span className="font-semibold">Link direto de arquivo</span> (.mp4, .webm) — player de vídeo embutido na página.</span>
              <p className="text-blue-600 mt-0.5">Se o arquivo estiver no Google Drive, compartilhe-o como <span className="font-semibold">"Qualquer pessoa com o link"</span> — caso contrário o vídeo não será executado no site.</p>
            </div>
          </div>
        </div>
        <p className="text-blue-600 pt-1">Deixe o campo vazio para ocultar a seção no site.</p>
      </div>
      {url && (
        <div className="rounded-xl overflow-hidden border border-gray-100 shadow-sm aspect-video">
          {url.includes('youtu') ? (
            <iframe
              src={`https://www.youtube.com/embed/${extractYouTubeId(url)}`}
              className="w-full h-full"
              allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
              allowFullScreen
            />
          ) : (
            <video src={url} controls className="w-full h-full object-cover" />
          )}
        </div>
      )}
      <SaveBar dirty={dirty} saving={saving} onSave={handleSave} />
    </div>
  );
}

function extractYouTubeId(url: string): string {
  const patterns = [
    /youtu\.be\/([^?&]+)/,
    /youtube\.com\/watch\?v=([^&]+)/,
    /youtube\.com\/embed\/([^?&]+)/,
  ];
  for (const p of patterns) {
    const m = url.match(p);
    if (m) return m[1];
  }
  return url;
}

// ── Main ───────────────────────────────────────────────────────────────────────
type TabId = 'cabecalho' | 'oque' | 'como' | 'paraquem' | 'cta' | 'video';

const TABS: { id: TabId; label: string; icon: LucideIcon }[] = [
  { id: 'cabecalho', label: 'Cabeçalho',    icon: ClipboardList },
  { id: 'oque',      label: 'O que é',      icon: FileText },
  { id: 'como',      label: 'Como Funciona',icon: Search },
  { id: 'paraquem',  label: 'Para Quem é',  icon: Users },
  { id: 'cta',       label: 'CTA',          icon: ArrowRight },
  { id: 'video',     label: 'Vídeo',        icon: Play },
];

export function AvaliacoesManager() {
  const [activeTab, setActiveTab] = useState<TabId>('cabecalho');

  return (
    <div className="space-y-6">
      {/* Visibility toggle */}
      <PageVisibilityToggle pageKey="page_avaliacoes_visible" label="Página de Avaliação de Caso" />

      {/* Tab bar */}
      <div className="flex gap-1 bg-gray-100 p-1 rounded-xl flex-wrap">
        {TABS.map(tab => (
          <button
            key={tab.id}
            onClick={() => setActiveTab(tab.id)}
            className={`flex-1 flex items-center justify-center px-3 py-2 rounded-lg text-xs font-semibold transition-all min-w-[80px] ${
              activeTab === tab.id ? 'bg-white text-brand-primary shadow-sm' : 'text-gray-500 hover:text-gray-700'
            }`}
          >
            {tab.label}
          </button>
        ))}
      </div>

      {activeTab === 'cabecalho' && <CabecalhoTab />}
      {activeTab === 'oque'      && <OQueETab />}
      {activeTab === 'como'      && <ComoFuncionaTab />}
      {activeTab === 'paraquem'  && <ParaQuemTab />}
      {activeTab === 'cta'       && <CtaTab />}
      {activeTab === 'video'     && <VideoTab />}
    </div>
  );
}
